<script setup lang="ts">
import { ref, Ref, computed } from 'vue';
import DataTable, { DataTableFilterMeta } from 'primevue/datatable';
import { FilterMatchMode } from 'primevue/api';
import { useRelayHubStore } from '@/store';
import { EMIT } from '@/enum';
import { useArrayUtilities } from '@/composables';
import AddStatesModal from './AddStatesModal.vue';
import { storeToRefs } from 'pinia';

const relayHubStore = useRelayHubStore();
const { selectedMedicaidStates, originalMedicaidStates, enabledStates, loading } = storeToRefs(relayHubStore);
const showAddStates = ref<boolean>(false);

const emit = defineEmits<{
  (e: EMIT.RESET): void;
}>();

const selectedItems = ref<string[]>([]);

const openAddStates = () => (showAddStates.value = true);

const closeAddStates = () => (showAddStates.value = false);

const onCancel = async () => {
  emit(EMIT.RESET);
};

const onSave = async () => {
  await relayHubStore.saveStateMedicareAssignments();
  if (relayHubStore.error) relayHubStore.doToast(relayHubStore.error as string, 'Error', true);
  else relayHubStore.doToast('State Changes Saved');

  emit(EMIT.RESET);
};

const handleStatesAdded = () => {
  closeAddStates();
  emit(EMIT.RESET);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filter: Ref<any> = ref({
  global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  name: { value: '', matchMode: FilterMatchMode.STARTS_WITH },
} as DataTableFilterMeta);

const shouldDisable = computed(() =>
  useArrayUtilities().twoNumericArraysContainSameData(selectedMedicaidStates.value, originalMedicaidStates.value),
);
</script>

<template>
  <Card class="page-content-full">
    <template #header>
      <div class="dialog__header flex align-items-center justify-content-between w-full">
        <h2>States</h2>
        <div class="flex justify-content-end gap-2">
          <div class="ml-auto">
            <AppButton type="text" size="small" text="Add States" icon-left="add" @click="openAddStates" />
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <AddStatesModal v-if="showAddStates" @cancel="closeAddStates" @save="handleStatesAdded" />
      <Loader v-if="loading" class="flex justify-content-center align-items-center h-full loader" />
      <DataTable
        v-model:filters="filter"
        v-model:selection="selectedItems"
        :global-filter-fields="['name']"
        :scrollable="true"
        :value="enabledStates"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="flex"
        :style="{ maxHeight: '600px' }"
      >
        <template #empty>There are no States connected</template>
        <template v-if="enabledStates?.length">
          <Column class="text-center justify-content-center px-3">
            <template #body="slotProps">
              <Checkbox v-model="selectedMedicaidStates" :name="slotProps.data.name" :value="slotProps.data.id" />
            </template>
          </Column>
          <Column
            field="name"
            sortable
            header="Name"
            :style="{
              minWidth: '88%',
            }"
          >
            <template #body="slotProps">
              <div class="flex w-full gap-1">
                <div v-text="slotProps.data.name"></div>
              </div>
            </template>
          </Column>
        </template>
      </DataTable>
    </template>
    <template #footer>
      <AppButton text="Cancel" type="secondary" :disabled="shouldDisable" @click="onCancel" />
      <AppButton text="Save" type="primary" :disabled="shouldDisable" @click="onSave" />
    </template>
  </Card>
</template>

<style scoped lang="scss"></style>
